import { useRef, ReactElement } from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parseISO, format, formatISO, isValid } from 'date-fns';
import get from 'lodash/get';
import IconButton from '@material-ui/core/IconButton';
import EventIcon from '@mui/icons-material/Event';

import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';

import styles from './DatePicker.module.scss';

interface IProps {
  control: Control<any>;
  errors: FieldErrors;
  name: string;
  minDate?: Date;
  maxDate?: Date;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showTimeSelect?: boolean;
  dateFormat?: string;
  isDateOnly?: boolean;
  isInBody?: boolean;
}

const DatePickerComponent = ({
  control,
  errors,
  name,
  minDate,
  maxDate,
  label = '',
  required = false,
  placeholder = '',
  disabled = false,
  showTimeSelect = false,
  dateFormat = 'MM/dd/yyyy',
  isDateOnly = false,
  isInBody = false,
}: IProps): ReactElement => {
  const startRef = useRef<DatePicker>();

  const error = get(errors, `${name}.message`, '');

  const handleChange = (date: [Date, Date] | Date) => {
    if (Array.isArray(date) || !isValid(date)) {
      return isDateOnly ? null : '';
    }

    return isDateOnly ? format(date, 'y-MM-dd') : formatISO(date);
  };

  const formatValue = (val: any) => {
    const date = parseISO(val);

    if (!isValid(date)) return undefined;
    return date;
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <FormControl required={required} component="fieldset" className={styles.datePickerControl}>
          <FormLabel component="legend" className={styles.label}>
            {label}
          </FormLabel>
          <DatePicker
            ref={startRef}
            className={styles.datePicker}
            showTimeSelect={showTimeSelect}
            timeFormat="HH:mm"
            timeIntervals={1}
            timeCaption="Time"
            calendarStartDay={1}
            dateFormat={dateFormat}
            selected={formatValue(value)}
            onChange={val => onChange(handleChange(val))}
            minDate={minDate}
            maxDate={maxDate}
            placeholderText={placeholder}
            disabled={disabled}
            portalId={isInBody ? 'root-portal' : null}
          />
          <IconButton className={styles.icon} onClick={() => startRef.current.setOpen(true)}>
            <EventIcon />
          </IconButton>
          {error && <FormHelperText error>{error}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

export default DatePickerComponent;
